import { format, parseISO } from 'date-fns';
import { HealthCheckResult, HealthCheckRow } from './systemHealthConstants';

export interface CategoryGroup {
  category: string;
  checks: HealthCheckResult[];
}

export const groupByCategory = (checks: HealthCheckResult[]): CategoryGroup[] => {
  const order: string[] = [];
  const byCategory = new Map<string, HealthCheckResult[]>();

  checks.forEach(check => {
    if (!byCategory.has(check.category)) {
      order.push(check.category);
      byCategory.set(check.category, []);
    }
    byCategory.get(check.category)?.push(check);
  });

  return order.map(category => ({ category, checks: byCategory.get(category) ?? [] }));
};

export const formatLastRunAt = (lastRunAt: string | null): string => {
  if (!lastRunAt) {
    return 'Never run';
  }
  return format(parseISO(lastRunAt), 'M/d/yyyy h:mm a');
};

export const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
};

// Tab-separated so pasting into a spreadsheet (Excel/Sheets) splits into columns.
export const rowsToTsv = (rows: HealthCheckRow[]): string => {
  const columns = Object.keys(rows[0] ?? {});
  const lines = [columns, ...rows.map(row => columns.map(column => formatCellValue(row[column])))];
  return lines.map(line => line.join('\t')).join('\n');
};

export type RowFix =
  | { type: 'task'; taskName: string; enabled: boolean }
  | { type: 'commission'; company: string; month: string; commission: number };

// Some checks (Finance Commission Updates, Linear/synthetic-control/frequency-by-IP job
// enable-disable mismatches) carry a structured, one-click-applicable fix -- see
// bpm-lambdas misc/systemHealth.ts's `desired_enabled`/`commission` columns and
// applySystemHealthFixInternal, which only ever runs a parameterized
// dag_tasks.enabled or commission_by_company_month.commission update built from these exact
// fields (never SQL text). A row with neither shape -- including the synthetic-control/
// frequency-by-IP checks' "task doesn't exist yet" rows, where desired_enabled is null -- has
// nothing to apply; it may still show a `sql`/`update_sql` column for manual copy-paste.
export const getRowFix = (row: HealthCheckRow): RowFix | null => {
  if (typeof row.task_name === 'string' && typeof row.desired_enabled === 'boolean') {
    return { type: 'task', taskName: row.task_name, enabled: row.desired_enabled };
  }
  if (
    typeof row.company === 'string' &&
    typeof row.month === 'string' &&
    typeof row.commission === 'number' &&
    Number.isFinite(row.commission)
  ) {
    return {
      type: 'commission',
      company: row.company,
      month: row.month,
      commission: row.commission
    };
  }
  return null;
};

// A stable identity for a fix, used to de-dupe/track "applied" state -- distinct task fixes and
// commission fixes can never collide since the two are prefixed differently.
export const getRowFixKey = (fix: RowFix): string =>
  fix.type === 'task' ? `task:${fix.taskName}` : `commission:${fix.company} ${fix.month}`;
